import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Textarea,
  Select,
  Calendar,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@clinic-os/ui';
import { TimeSlotPicker } from '../features/appointments/TimeSlotPicker';
import { BookingSuccessView } from '../features/appointments/BookingSuccessView';
import { appointmentService } from '../services/appointmentService';
import { DEMO_CLINIC_ID } from '../data/demo-data';
import type { Appointment } from '@clinic-os/shared/types';

const APPOINTMENT_TYPES = [
  { value: 'checkup', label: 'Checkup' },
  { value: 'cleaning', label: 'Cleaning / Prophylaxis' },
  { value: 'filling', label: 'Filling' },
  { value: 'extraction', label: 'Extraction' },
  { value: 'root_canal', label: 'Root Canal' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'xray', label: 'X-Ray' },
  { value: 'other', label: 'Other' },
];

interface ClinicInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
}

// Mock clinic data - in production this would come from an API based on clinicSlug
const MOCK_CLINICS: Record<string, ClinicInfo> = {
  'demo': {
    name: 'Smile Dental Clinic',
    address: '123 Medical Center Rd, Bangkok 10310',
    phone: '+66 81 234 5678',
    email: 'contact@smiledentalclinic.com',
  },
  'default': {
    name: 'HealthPlus Clinic',
    address: '456 Wellness Avenue, Bangkok 10500',
    phone: '+66 82 345 6789',
    email: 'info@healthplusclinic.com',
  },
};

interface BookingFormData {
  type: string;
  date: Date | null;
  time: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
}

const initialFormData: BookingFormData = {
  type: '',
  date: null,
  time: null,
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  notes: '',
};

function StepIndicator({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
        <React.Fragment key={step}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              step === currentStep
                ? 'bg-blue-600 text-white'
                : step < currentStep
                ? 'bg-green-500 text-white'
                : 'bg-slate-200 text-slate-500'
            }`}
          >
            {step < currentStep ? <Check className="w-4 h-4" /> : step}
          </div>
          {step < totalSteps && (
            <div
              className={`w-8 h-0.5 ${
                step < currentStep ? 'bg-green-500' : 'bg-slate-200'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function ClinicHeader({ clinic }: { clinic: ClinicInfo }) {
  return (
    <div
      className="bg-blue-600 text-white py-8 px-4"
      data-appdom-id="booking-header"
      data-appdom-type="Header"
      data-appdom-scope="header"
    >
      <div className="max-w-md mx-auto">
        <h1
          className="text-2xl font-semibold mb-2"
          data-appdom-id="booking-clinic-name"
          data-appdom-type="Heading"
          data-appdom-scope="header"
        >
          {clinic.name}
        </h1>
        <div className="space-y-1 text-white">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">{clinic.address}</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            <span className="text-sm">{clinic.phone}</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            <span className="text-sm">{clinic.email}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PublicBookingPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<BookingFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState<Appointment | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clinic = MOCK_CLINICS[clinicSlug ?? 'default'] ?? MOCK_CLINICS['default']!;
  const totalSteps = 4;

  const handleTypeSelect = (type: string) => {
    setFormData((prev) => ({ ...prev, type }));
    setCurrentStep(2);
  };

  const handleDateSelect = (date: Date) => {
    setFormData((prev) => ({ ...prev, date, time: null }));
  };

  const handleTimeSelect = (time: string) => {
    setFormData((prev) => ({ ...prev, time }));
    setCurrentStep(3);
  };

  const handlePatientInfoChange = (field: keyof BookingFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleContinueToSummary = () => {
    if (formData.firstName && formData.lastName && formData.phone) {
      setCurrentStep(4);
    }
  };

  const handleGoBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.type || !formData.date || !formData.time || !formData.firstName || !formData.lastName) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const scheduledAt = new Date(`${formData.date.toISOString().split('T')[0]}T${formData.time}:00`).toISOString();
      const patientName = `${formData.firstName} ${formData.lastName}`;

      const result = await appointmentService.create({
        clinicId: DEMO_CLINIC_ID,
        patientId: undefined, // For public booking, patientId is not required initially
        practitionerId: 'dr1',
        status: 'scheduled',
        scheduledAt,
        durationMinutes: 30,
        type: APPOINTMENT_TYPES.find((t) => t.value === formData.type)?.label ?? formData.type,
        notes: formData.notes,
      });

      setBookingResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to book appointment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookAnother = () => {
    setFormData(initialFormData);
    setCurrentStep(1);
    setBookingResult(null);
    setError(null);
  };

  const getSelectedTypeLabel = () => {
    return APPOINTMENT_TYPES.find((t) => t.value === formData.type)?.label ?? '';
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (time: string | null) => {
    if (!time) return '';
    const parts = time.split(':').map(Number);
    const hours = parts[0]!;
    const minutes = parts[1]!;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Show success view after booking
  if (bookingResult) {
    return (
      <main className="min-h-screen bg-slate-50">
        <ClinicHeader clinic={clinic} />
        <div className="max-w-md mx-auto px-4 py-8">
          <BookingSuccessView
            appointmentType={bookingResult.type}
            appointmentDate={new Date(bookingResult.scheduledAt)}
            appointmentTime={
              new Date(bookingResult.scheduledAt).toTimeString().slice(0, 5)
            }
            patientName={formData.firstName + ' ' + formData.lastName}
            clinicName={clinic.name}
            clinicPhone={clinic.phone}
            clinicAddress={clinic.address}
            onBookAnother={handleBookAnother}
          />
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50" data-appdom-id="booking-page" data-appdom-type="Container" data-appdom-scope="main">
      <ClinicHeader clinic={clinic} />

      <div className="max-w-md mx-auto px-4 py-6">
        <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />

        {/* Step 1: Select Appointment Type */}
        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Select Appointment Type</CardTitle>
              <CardDescription>Choose the type of appointment you need</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {APPOINTMENT_TYPES.map((type) => (
                <Button
                  key={type.value}
                  variant={formData.type === type.value ? 'primary' : 'outline'}
                  className="w-full justify-start"
                  onClick={() => handleTypeSelect(type.value)}
                >
                  {type.label}
                </Button>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Date and Time */}
        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Select Date and Time</CardTitle>
              <CardDescription>Choose your preferred appointment date and time</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="mb-2 block">Select Date</Label>
                <Calendar
                  selected={formData.date ?? undefined}
                  onSelect={handleDateSelect}
                  className="w-full"
                />
              </div>

              {formData.date && (
                <div>
                  <Label className="mb-2 block">
                    Available Time Slots for {formData.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </Label>
                  <TimeSlotPicker
                    selectedDate={formData.date}
                    selectedTime={formData.time}
                    onSelect={handleTimeSelect}
                    clinicId={DEMO_CLINIC_ID}
                  />
                </div>
              )}

              <Button variant="ghost" onClick={handleGoBack} className="w-full">
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Patient Information */}
        {currentStep === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>Your Information</CardTitle>
              <CardDescription>Enter your contact details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handlePatientInfoChange('firstName', e.target.value)}
                    placeholder="John"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handlePatientInfoChange('lastName', e.target.value)}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handlePatientInfoChange('phone', e.target.value)}
                  placeholder="+66 81 234 5678"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handlePatientInfoChange('email', e.target.value)}
                  placeholder="john.doe@example.com"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handlePatientInfoChange('notes', e.target.value)}
                  placeholder="Any special requests or information we should know..."
                  rows={3}
                />
              </div>

              {error && (
                <p className="text-sm text-red-700">{error}</p>
              )}

              <div className="flex gap-3">
                <Button variant="ghost" onClick={handleGoBack} className="flex-1">
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
                <Button
                  onClick={handleContinueToSummary}
                  disabled={!formData.firstName || !formData.lastName || !formData.phone}
                  className="flex-1"
                >
                  Continue
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Confirmation Summary */}
        {currentStep === 4 && (
          <Card>
            <CardHeader>
              <CardTitle>Confirm Your Appointment</CardTitle>
              <CardDescription>Please review your appointment details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Appointment Type</span>
                  <span className="font-medium text-slate-900">{getSelectedTypeLabel()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Date</span>
                  <span className="font-medium text-slate-900">{formatDate(formData.date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Time</span>
                  <span className="font-medium text-slate-900">{formatTime(formData.time)}</span>
                </div>
                <div className="border-t border-slate-200 pt-3 mt-3">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Patient Name</span>
                    <span className="font-medium text-slate-900">
                      {formData.firstName} {formData.lastName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Phone</span>
                    <span className="font-medium text-slate-900">{formData.phone}</span>
                  </div>
                  {formData.email && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Email</span>
                      <span className="font-medium text-slate-900">{formData.email}</span>
                    </div>
                  )}
                </div>
                {formData.notes && (
                  <div className="border-t border-slate-200 pt-3 mt-3">
                    <span className="text-slate-500 block mb-1">Notes</span>
                    <span className="text-sm text-slate-700">{formData.notes}</span>
                  </div>
                )}
              </div>

              {error && (
                <p className="text-sm text-red-700">{error}</p>
              )}

              <div className="flex gap-3">
                <Button variant="ghost" onClick={handleGoBack} className="flex-1" disabled={isSubmitting}>
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="flex-1"
                  disabled={isSubmitting}
                  data-appdom-id="booking-submit-cta"
                  data-appdom-type="Button"
                  data-appdom-role="primary"
                  data-appdom-scope="main"
                >
                  {isSubmitting ? 'Booking...' : 'Book Appointment'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-sm text-slate-500 mt-6">
          By booking an appointment, you agree to our terms of service.
        </p>
      </div>
    </main>
  );
}
